import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Theme,
  Typography,
} from '@material-ui/core';
import React, { useState, useMemo } from 'react';
import ButtonProgress from 'src/components/ButtonProgress';
import { makeStyles } from '@material-ui/styles';
import {
  DataFormatter,
  SingleDealModalProps,
} from 'src/services/DealcloudV2/types';
import { useGetFields } from './hooks/useGetFields';
import { useOrganizationPropsValue } from 'src/hooks';
import DealCloudForm from './components/DealCloudForm';
import { useCreateDeal } from './hooks/useCreateDeal';
import { FieldType } from './type';
import classNames from 'classnames';

interface DealCloudV2ModalProps {
  open: boolean;
  onRequestClose: () => void;
  data: {
    propertyInfo: {
      propertyDealData: SingleDealModalProps;
      assets: DataFormatter;
    };
    mode: 'create' | 'edit';
  };
  shareStatus: string;
}

const useStyles = makeStyles<Theme>(() => ({
  modal: {
    minWidth: 515,
    overflow: 'visible',
    borderRadius: 10,
  },
  dialogTitle: {
    fontWeight: 700,
    fontSize: 20,
    padding: 20,
  },
  modalContent: {
    maxWidth: 515,
  },
  key: {
    fontWeight: 500,
    fontSize: 14,
  },
  value: {
    fontWeight: 700,
    fontSize: 14,
  },
  address: {
    marginBottom: '20px',
  },
  divider: {
    marginTop: '40px',
    marginBottom: '40px',
    backgroundColor: '#BDBDBD',
  },
}));

const DealCloudV2Modal: React.FC<DealCloudV2ModalProps> = (props) => {
  const [propertyFormState, setPropertyFormState] = useState({});
  const [dealFormState, setDealFormState] = useState({});
  const { propertyDealData } = props.data.propertyInfo;
  const classes = useStyles();
  const fieldData = useGetFields();

  const isCreateBoth =
    useOrganizationPropsValue('dealcloud_create_mode') ===
    'create_property_and_deal';

  const taxAssessorField = fieldData.propertyFields.find(
    (field) => field.isTaxAssessorIdField
  );
  const { submit } = useCreateDeal(
    {
      propertyFormState: {
        ...propertyFormState,
        [taxAssessorField.dealCloudField.apiName]:
          propertyDealData.taxAssessorId,
      },
      dealFormState,
    },
    isCreateBoth,
    props.onRequestClose
  );

  const isValid = () => {
    const requiredPropertyFields = fieldData.propertyFields
      .filter((field) => field.createEditDialogOptions)
      .map((field) =>
        field.dealCloudField.isRequired ? field.dealCloudField.apiName : null
      )
      .filter((value) => value !== null);
    if (
      requiredPropertyFields.some(
        (field) =>
          propertyFormState[field] === '' ||
          propertyFormState[field] === null ||
          propertyFormState[field] === undefined
      )
    ) {
      return false;
    } else if (isCreateBoth) {
      const requiredDealFields = fieldData.dealFields
        .filter((field) => field.createEditDialogOptions)
        .map((field) =>
          field.dealCloudField.isRequired ? field.dealCloudField.apiName : null
        )
        .filter((value) => value !== null);
      if (
        requiredDealFields.some(
          (field) =>
            dealFormState[field] === '' ||
            dealFormState[field] === null ||
            dealFormState[field] === undefined
        )
      ) {
        return false;
      }
    }
    return true;
  };

  const propertyValues = useMemo(() => {
    const currentValues = {};
    if (fieldData) {
      fieldData.propertyFields.forEach((field) => {
        if (field.mapToCherreField) {
          if (propertyDealData[field.mapToCherreField]) {
            if (field.createEditDialogOptions.type === FieldType.NUMBER) {
              currentValues[field.dealCloudField.apiName] =
                Number(
                  propertyDealData[field.mapToCherreField].replace(/,/g, '')
                ) ?? '';
            } else if (
              field.createEditDialogOptions.type === FieldType.SELECT ||
              field.createEditDialogOptions.type === FieldType.RADIO
            ) {
              const option = field.options.find(
                (opt) => opt.name === propertyDealData[field.mapToCherreField]
              );
              if (option) {
                currentValues[field.dealCloudField.apiName] = option.id;
              }
            } else {
              currentValues[field.dealCloudField.apiName] =
                propertyDealData[field.mapToCherreField];
            }
          }
        }
      });
    }
    return currentValues;
  }, [fieldData]);

  return (
    <Dialog
      open={props.open}
      onClose={props.onRequestClose}
      className={`js-${props.data.mode}-deal-modal`}
      classes={{
        paper: classes.modal,
      }}
    >
      <Typography className={classes.dialogTitle}>
        {props.data.mode === 'create'
          ? isCreateBoth
            ? 'Create Deal'
            : 'Create Property'
          : 'Edit Deal'}
      </Typography>
      <DialogContent className={classes.modalContent}>
        <Typography className={classes.key}>Property Address</Typography>
        <Typography className={classNames(classes.value, classes.address)}>
          {propertyDealData.propertyAddress}
        </Typography>
        <DealCloudForm
          form={propertyFormState}
          setForm={setPropertyFormState}
          fields={fieldData.propertyFields
            .filter((field) => field.createEditDialogOptions)
            .map((field) => ({
              name: field.dealCloudField.apiName,
              isRequired: field.dealCloudField.isRequired,
              label: field.label,
              isAddress: field.isAddress ?? false,
              currentValue: propertyValues[field.dealCloudField.apiName] ?? '',
              createEditDialogOptions: field.createEditDialogOptions,
              options: field.options ?? [],
            }))}
        />
        {isCreateBoth && (
          <>
            <Divider className={classes.divider} />
            <DealCloudForm
              form={dealFormState}
              setForm={setDealFormState}
              fields={fieldData.dealFields
                .filter((field) => field.createEditDialogOptions)
                .map((field) => ({
                  name: field.dealCloudField.apiName,
                  isRequired: field.dealCloudField.isRequired,
                  label: field.label,
                  createEditDialogOptions: field.createEditDialogOptions,
                  options: field.options ?? [],
                }))}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <div style={{ padding: 12 }}>
          <Button onClick={props.onRequestClose} color='primary'>
            Cancel
          </Button>
          <ButtonProgress
            className='js-deal-submit-button'
            disabled={!isValid()}
            onClick={async () => {
              await submit();
            }}
            variant='contained'
            color='primary'
          >
            {props.data.mode === 'create' ? 'Create' : 'Done'}
          </ButtonProgress>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default DealCloudV2Modal;
